import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Form, Input, message, Modal, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	brandsRestCrudThunksForBuyer,
	brandsRestCrudThunksForSupplier,
} from '../../thunks/brands_thunks';
import OWUpload from '../OWUpload';
import { nonHeicImageUploadValidation } from '../../utils/ImageUtils';
import { getProtectedImageUriFromString } from '../../utils/FileAccessUtils';
import { useForm } from 'antd/lib/form/Form';

interface AddOrEditBrandPopupProps {
	formData?: any;
	onCancel: () => void;
	create: (entity: any, primaryKey: string, targetCollectionName?: string) => Promise<any>;
	update: (entity: any) => Promise<any>;
	userType: string;
	targetCollectionName?: string;
}

const AddOrEditBrandPopup: FC<AddOrEditBrandPopupProps> = ({
	formData,
	onCancel,
	create,
	update,
	userType,
	targetCollectionName,
}): React.ReactElement => {
	const [form] = useForm();
	const { setFieldsValue } = form;

	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);

	const logoAttachment = Form.useWatch('logoAttachment', form);

	const isUpdate = useMemo(() => nullSafeGet('id', formData), [formData]);
	const submitMethod = useMemo(() => (isUpdate ? update : create), [create, isUpdate, update]);

	const logoLink = useMemo(
		() =>
			nullSafeGet('fileId', logoAttachment)
				? getProtectedImageUriFromString(userType)(
						`${nullSafeGet('fileId', logoAttachment)}/${nullSafeGet('fileName', logoAttachment)}`
				  )
				: '',
		[logoAttachment, userType]
	);

	const onSubmit = useCallback(
		(values) => {
			const entity = formData && isUpdate ? { ...formData, ...values } : { ...values };
			setSaving(true);
			submitMethod(entity, 'id', targetCollectionName)
				.then(() => {
					message.success(`Brand ${isUpdate ? 'updated' : 'added'} successfully!`);
					onCancel();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[formData, isUpdate, onCancel, submitMethod, targetCollectionName]
	);

	const handleUploadChange = useCallback(
		(info) => {
			if (info.file.status === 'uploading') {
				setUploading(true);
				return;
			}
			if (info.file.status === 'done') {
				const fileName = nullSafeGetOrElse('file.response.data.fileName', info, '');
				const fileId = nullSafeGetOrElse('file.response.data.fileId', info, '');
				setUploading(false);
				setFieldsValue({ logoAttachment: { fileName, fileId } });
			}
		},
		[setFieldsValue]
	);

	const onImageUploadError = useCallback(() => {
		message.error('Image upload failed! Please contact support!');
		setUploading(false);
	}, []);

	return (
		<Modal
			visible={true}
			title={`${isUpdate ? 'Edit' : 'New'} Brand`}
			closable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onCancel}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={saving}
					disabled={uploading}
					onClick={() => form.submit()}
				>
					{isUpdate ? 'Update' : 'Create'}
				</Button>,
			]}
		>
			<Form
				layout="vertical"
				initialValues={{ ...(formData || {}) }}
				onFinish={onSubmit}
				form={form}
			>
				<Form.Item
					name="brandName"
					label="Name"
					rules={[{ required: true, message: 'Please give your brand a name.' }]}
				>
					<Input style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item label="Logo" name="logoAttachment">
					<OWUpload
						accept="image/*"
						name="file"
						listType="picture-card"
						className="avatar-uploader"
						showUploadList={false}
						beforeUpload={nonHeicImageUploadValidation}
						onChange={handleUploadChange}
						onError={onImageUploadError}
					>
						{logoLink ? (
							<img
								src={logoLink}
								className="h-auto w-20 object-contain"
								style={{ maxHeight: 104 }}
								alt=""
							/>
						) : (
							<div>
								{uploading ? <Spin /> : <PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						)}
					</OWUpload>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	create: (entity, primaryKey, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.create(entity, primaryKey, targetCollectionName)
				: brandsRestCrudThunksForBuyer.create(entity, primaryKey, targetCollectionName)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.update(entity)
				: brandsRestCrudThunksForBuyer.update(entity)
		),
});

const ComponentWithoutUserType = connect(null, mapDispatchToProps)(AddOrEditBrandPopup as any);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
